import { PieChartOutlined } from "@ant-design/icons";
import { Card, Col, Row, Select } from "antd";
import { useEffect, useState } from "react";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import { XAxis, YAxis, CartesianGrid, Tooltip, Bar, BarChart, Legend, ResponsiveContainer, } from "recharts";

const UserAndBookingChart = () => {
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [period, setPeriod] = useState("yearly");
    const [activeUsersData, setActiveUsersData] = useState([]);
    const [completedBookingData, setCompletedBookingData] = useState([]);
    const [subscriptionChartData, setSubscriptionChartData] = useState([]);
    const [branchOptions, setBranchOptions] = useState([]);
    const { request } = useRequest();

    const MONTH_ORDER = {
        January: 1,
        February: 2,
        March: 3,
        April: 4,
        May: 5,
        June: 6,
        July: 7,
        August: 8,
        September: 9,
        October: 10,
        November: 11,
        December: 12,
    };

    const api = {
        branchList: apiPath.branchList,
        activeUsers: apiPath.activeUsers,
        completedBookings: apiPath.completedBookings,
        subscribeUsers: apiPath.subscribeUsers,
        unSubscribeUsers: apiPath.unSubscribeUsers,
    };

    const fetchBranches = () => {
        request({
            url: api.branchList,
            method: "GET",
            onSuccess: ({ data }) => {
                const options =
                    data?.data?.map((item) => ({
                        label: item.name,
                        value: item._id,
                    })) || [];
                setBranchOptions(options);
            },
            onError: (err) => ShowToast(err, Severty.ERROR),
        });
    };

    const fetchActiveUsers = () => {
        let url = `${api.activeUsers}?period=${period}`;

        if (selectedBranch) {
            url += `&branchId=${selectedBranch}`;
        }

        request({
            url,
            method: "GET",
            onSuccess: ({ data }) => {
                setActiveUsersData(data?.data || []);
            },
            onError: (err) => ShowToast(err, Severty.ERROR),
        });
    };

    const fetchCompletedBookings = () => {
        let url = `${api.completedBookings}?period=${period}`;

        if (selectedBranch) {
            url += `&branchId=${selectedBranch}`;
        }

        request({
            url,
            method: "GET",
            onSuccess: ({ data }) => {
                setCompletedBookingData(data?.data || []);
            },
            onError: (err) => ShowToast(err, Severty.ERROR),
        });
    };

    const fetchSubscriptionUsers = () => {
        let subscribeUrl = `${api.subscribeUsers}?period=${period}`;
        let unSubscribeUrl = `${api.unSubscribeUsers}?period=${period}`;

        if (selectedBranch) {
            subscribeUrl += `&branchId=${selectedBranch}`;
            unSubscribeUrl += `&branchId=${selectedBranch}`;
        }

        Promise.all([
            new Promise((resolve, reject) =>
                request({
                    url: subscribeUrl,
                    method: "GET",
                    onSuccess: ({ data }) => resolve(data?.data || []),
                    onError: reject,
                })
            ),
            new Promise((resolve, reject) =>
                request({
                    url: unSubscribeUrl,
                    method: "GET",
                    onSuccess: ({ data }) => resolve(data?.data || []),
                    onError: reject,
                })
            ),
        ])
            .then(([subscribed, unSubscribed]) => {
                const map = {};

                subscribed.forEach(item => {
                    map[item.name] = {
                        name: item.name,
                        subscribed: item.totalUsers,
                        unSubscribed: 0,
                    };
                });

                unSubscribed.forEach(item => {
                    if (!map[item.name]) {
                        map[item.name] = {
                            name: item.name,
                            subscribed: 0,
                            unSubscribed: item.totalUsers,
                        };
                    } else {
                        map[item.name].unSubscribed = item.totalUsers;
                    }
                });

                const sortedData = Object.values(map).sort((a, b) => {
                    if (period === "weekly") {
                        const aWeek = Number(a.name.replace("Week ", ""));
                        const bWeek = Number(b.name.replace("Week ", ""));
                        return aWeek - bWeek;
                    }
                    const [aMonth, aYear] = a.name.split(" ");
                    const [bMonth, bYear] = b.name.split(" ");
                    if (aYear !== bYear) {
                        return Number(aYear) - Number(bYear);
                    }
                    return MONTH_ORDER[aMonth] - MONTH_ORDER[bMonth];
                });
                setSubscriptionChartData(sortedData);
            })
            .catch(err => ShowToast(err, Severty.ERROR));
    };

    useEffect(() => {
        fetchBranches();
        fetchActiveUsers();
        fetchCompletedBookings();
        fetchSubscriptionUsers();
    }, []);

    useEffect(() => {
        fetchActiveUsers();
        fetchCompletedBookings();
        fetchSubscriptionUsers();
    }, [selectedBranch, period]);

    return (
        <>
            <Card className="mt-3">
                <div className="new-flex-mainjh">
                    <Select
                        placeholder="Select Branch"
                        style={{ width: "100%" }}
                        options={branchOptions}
                        value={selectedBranch}
                        onChange={setSelectedBranch}
                        allowClear
                    />
                    <Select
                        value={period}
                        style={{ width: "100%" }}
                        onChange={setPeriod}
                        options={[
                            { value: "weekly", label: "Weekly" },
                            { value: "monthly", label: "Monthly" },
                            { value: "yearly", label: "Yearly" },
                            { value: "last5year", label: "Last 5 Year" },
                        ]}
                    />


                </div>
                <Row gutter={[16, 16]} className="mb-24">



                    {/* ACTIVE USERS */}
                    <Col span={24} md={12}>
                        <Card title={<><PieChartOutlined /> Active Users</>}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={activeUsersData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar maxBarSize={50} name="Active Users" fill="#ff5722" dataKey="totalUsers" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>

                    {/* COMPLETED BOOKINGS */}
                    <Col span={24} md={12}>
                        <Card title={<><PieChartOutlined /> Completed Bookings</>}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={completedBookingData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar maxBarSize={50} name=" Completed Bookings" fill="#ff5722" dataKey="totalBookings" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>

                    {/* SUBSCRIPTION USERS */}
                    <Col span={24}>
                        <Card title={<><PieChartOutlined /> Subscribe  & UnSubscribe  Users</>}>
                            <ResponsiveContainer width="100%" height={380}>
                                <BarChart data={subscriptionChartData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />

                                    <Bar
                                        dataKey="subscribed"
                                        fill="#ff5722"
                                        name="Subscribed Users"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={50}
                                    />

                                    <Bar
                                        dataKey="unSubscribed"
                                        fill="#9e9e9e"
                                        name="Unsubscribed Users"
                                        radius={[6, 6, 0, 0]}
                                        maxBarSize={50}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>

                </Row>
            </Card>
        </>
    );
};

export default UserAndBookingChart;
